// src/pages/AccountPage.tsx
// The main customer account dashboard: profile editing + order history.

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CatalogObject, Order } from "square";
import {
  getCustomerProfile,
  getCustomerOrders,
  getCustomerReturns,
  getCustomerCards,
  deleteCustomerCard,
  cancelCustomerReturn,
  updateCustomerProfile,
  CustomerProfile,
  ReturnRequest,
  SavedCard,
} from "../api/customer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useCart } from "../contexts/CartContext";
import { useCatalog } from "../contexts/CatalogContext";
import PageContainer, { GapSize } from "../components/PageContainer";
import OrderWrapper from "../components/OrderWrapper";
import ConfirmationModal from "../components/ConfirmationModal";
import Spinner from "../components/Spinner";
import Tooltip from "../components/Tooltip";
import {
  fulfillmentLabel,
  fulfillmentBadgeClass,
  fulfillmentDescription,
} from "../utils/fulfillmentStatus";
import {
  isSavedCardExpired,
  savedCardExpiry,
  savedCardLabel,
} from "../extensions/helpers";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const formatMoney = (amount?: number | bigint | null): string => {
  if (amount == null) return "—";
  return `$${(Number(amount) / 100).toFixed(2)}`;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface ProfileFormProps {
  profile: CustomerProfile;
  authToken: string;
  onSaved: (updated: CustomerProfile) => void;
}

const ProfileForm: React.FC<ProfileFormProps> = ({
  profile,
  authToken,
  onSaved,
}) => {
  const [firstName, setFirstName] = useState(profile.firstName);
  const [lastName, setLastName] = useState(profile.lastName);
  const [phone, setPhone] = useState(profile.phone);
  const [addressLine1, setAddressLine1] = useState(
    profile.address?.addressLine1 ?? "",
  );
  const [addressLine2, setAddressLine2] = useState(
    profile.address?.addressLine2 ?? "",
  );
  const [locality, setLocality] = useState(profile.address?.locality ?? "");
  const [adminLevel1, setAdminLevel1] = useState(
    profile.address?.administrativeDistrictLevel1 ?? "",
  );
  const [postalCode, setPostalCode] = useState(
    profile.address?.postalCode ?? "",
  );
  const [marketingAllowed, setMarketingAllowed] = useState(
    profile.marketingAllowed ?? false,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setSaved(false);
    setIsSaving(true);
    try {
      const result = await updateCustomerProfile(authToken, {
        firstName,
        lastName,
        phone,
        marketingAllowed,
        address: {
          addressLine1: addressLine1 || undefined,
          addressLine2: addressLine2 || undefined,
          locality: locality || undefined,
          administrativeDistrictLevel1: adminLevel1 || undefined,
          postalCode: postalCode || undefined,
          country: "US",
        },
      });
      if (!result || result.error || !result.data) {
        setSaveError(result?.error ?? "Failed to save profile.");
        return;
      }
      onSaved(result.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-2xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-brown-dark opensans-text">
            First Name
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="order-input-field"
            placeholder="Jane"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-brown-dark opensans-text">
            Last Name
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="order-input-field"
            placeholder="Doe"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-semibold text-brown-dark opensans-text">
          Email
        </label>
        <input
          type="email"
          value={profile.email}
          readOnly
          className="order-input-field opacity-60 cursor-not-allowed"
        />
        <p className="text-xs text-gray-500">Email cannot be changed here.</p>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-semibold text-brown-dark opensans-text">
          Phone
        </label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="order-input-field"
          placeholder="+1 (555) 000-0000"
        />
      </div>

      <div className="w-full h-px bg-accent-light" />

      {/* Not "Shipping Address" — this is the customer's OWN address (upsert-customer
          files the billing address on the Square customer record, because on a gift
          order the shipping address is where someone else lives). It pre-fills the
          shipping fields at checkout, which is exactly right when you're the
          recipient and is meant to be typed over when you aren't. */}
      <h3 className="text-base font-semibold text-brown-dark opensans-text">
        Saved Address
      </h3>
      <p className="text-xs text-gray-500 -mt-2">
        Your address, saved here to pre-fill checkout. Shipping somewhere else
        is still a per-order choice.
      </p>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-semibold text-brown-dark opensans-text">
          Street Address
        </label>
        <input
          type="text"
          value={addressLine1}
          onChange={(e) => setAddressLine1(e.target.value)}
          className="order-input-field"
          placeholder="123 Main St"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-semibold text-brown-dark opensans-text">
          Address Line 2
        </label>
        <input
          type="text"
          value={addressLine2}
          onChange={(e) => setAddressLine2(e.target.value)}
          className="order-input-field"
          placeholder="Apt, suite, unit (optional)"
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="flex flex-col gap-1 col-span-1">
          <label className="text-sm font-semibold text-brown-dark opensans-text">
            City
          </label>
          <input
            type="text"
            value={locality}
            onChange={(e) => setLocality(e.target.value)}
            className="order-input-field"
            placeholder="City"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-brown-dark opensans-text">
            State
          </label>
          <input
            type="text"
            value={adminLevel1}
            onChange={(e) =>
              setAdminLevel1(e.target.value.toUpperCase().slice(0, 2))
            }
            className="order-input-field"
            placeholder="NY"
            maxLength={2}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-brown-dark opensans-text">
            ZIP
          </label>
          <input
            type="text"
            value={postalCode}
            onChange={(e) =>
              setPostalCode(e.target.value.replace(/\D/g, "").slice(0, 5))
            }
            className="order-input-field"
            placeholder="10001"
            maxLength={5}
          />
        </div>
      </div>

      <div className="w-full h-px bg-accent-light" />

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={marketingAllowed}
          onChange={(e) => setMarketingAllowed(e.target.checked)}
          className="mt-0.5 accent-brown"
        />
        <span className="text-sm text-gray-700 opensans-text">
          I'd like to receive emails about new arrivals, promotions, and store
          news from Americana &amp; Company.
        </span>
      </label>

      {saveError && <p className="text-rust text-sm">{saveError}</p>}
      {saved && (
        <p className="text-green-700 text-sm">Profile saved successfully.</p>
      )}

      <button
        type="submit"
        disabled={isSaving}
        className="btn-primary self-start"
      >
        {isSaving ? "Saving…" : "Save Changes"}
      </button>
    </form>
  );
};

// Fetched order history, held outside the component so it survives navigating
// to an order detail page and back — AccountPage unmounts on a route change,
// which otherwise threw the list away and re-showed the "View order history"
// prompt. Deliberately in memory only (not localStorage): this is order PII and
// it should not outlive the tab. Cleared on sign-out.
let ordersCache: { customerId: string | null; orders: Order[] } | null = null;

// ---------------------------------------------------------------------------
// Main AccountPage
// ---------------------------------------------------------------------------

const AccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { session, isLoggedIn, isAdmin, logout, refreshSession } =
    useCustomerAuth();
  const { addToCart, setIsCartOpen } = useCart();
  const { items } = useCatalog();

  // Reuse anything already fetched this session (see ordersCache above).
  const cached =
    ordersCache && ordersCache.customerId === (session?.customerId ?? null)
      ? ordersCache.orders
      : null;

  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [orders, setOrders] = useState<Order[]>(cached ?? []);
  // Order history is NOT loaded with the rest of the page: getCustomerOrders is
  // the expensive call here (a Square order search plus a payment lookup per
  // order), and most visits to this page are for the profile or a return. The
  // customer asks for it, and we keep it for the rest of the session.
  const [ordersLoaded, setOrdersLoaded] = useState(cached !== null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [returns, setReturns] = useState<ReturnRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    "orders" | "returns" | "profile" | "payment"
  >("orders");
  // Cards on file. Loaded with the initial page data rather than on tab
  // activation, so switching tabs stays free (see onTabKeyDown).
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  // The card the customer is about to remove (drives the confirm modal) — a
  // removal is irreversible in Square, so it doesn't happen on a single click.
  const [cardToRemove, setCardToRemove] = useState<SavedCard | null>(null);
  const [cardBusy, setCardBusy] = useState(false);
  // The return request the customer is about to cancel (drives the modal).
  const [cancelTarget, setCancelTarget] = useState<ReturnRequest | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  // Per-order "Buy again" result note (keyed by order id).
  const [reorderMsg, setReorderMsg] = useState<{
    orderId: string;
    text: string;
    ok: boolean;
  } | null>(null);

  // Find the catalog product + variation for a past order's line-item variation
  // id, so a still-available item can be dropped back into the cart. Returns null
  // when the product/variation is gone (discontinued or hidden).
  const findCatalogEntry = useCallback(
    (
      variationId?: string | null,
    ): { product: CatalogObject; variation: CatalogObject } | null => {
      if (!variationId) return null;
      for (const product of items) {
        const variation = product.itemData?.variations?.find(
          (v) => v.id === variationId,
        );
        if (variation) return { product, variation };
      }
      return null;
    },
    [items],
  );

  // Re-add every still-available line item from a past order to the cart, then
  // open the cart. Unavailable items are skipped and reported.
  const handleReorder = (order: Order) => {
    if (!order.id) return;
    let added = 0;
    let missing = 0;
    for (const li of order.lineItems ?? []) {
      const entry = findCatalogEntry(li.catalogObjectId);
      const qty = Number(li.quantity ?? "1") || 1;
      if (entry) {
        addToCart(
          { product: entry.product, variation: entry.variation, quantity: 0 },
          qty,
        );
        added += 1;
      } else {
        missing += 1;
      }
    }
    if (added === 0) {
      setReorderMsg({
        orderId: order.id,
        text: "None of these items are still available to reorder.",
        ok: false,
      });
      return;
    }
    setReorderMsg({
      orderId: order.id,
      text:
        missing > 0
          ? `Added ${added} item${added === 1 ? "" : "s"} to your cart · ${missing} no longer available.`
          : `Added ${added} item${added === 1 ? "" : "s"} to your cart.`,
      ok: true,
    });
    setIsCartOpen(true);
  };

  const handleCancelReturn = async () => {
    if (!session?.authToken || !cancelTarget) return;
    setCancelBusy(true);
    const res = await cancelCustomerReturn(session.authToken, cancelTarget.id);
    setCancelBusy(false);
    if (res?.data) {
      // Reflect the new status locally without a full refetch.
      setReturns((prev) =>
        prev.map((r) => (r.id === res.data!.id ? res.data! : r)),
      );
      setCancelTarget(null);
    } else {
      setError(res?.error ?? "Could not cancel the return request.");
      setCancelTarget(null);
    }
  };

  const handleRemoveCard = async () => {
    if (!session?.authToken || !cardToRemove) return;
    setCardBusy(true);
    const res = await deleteCustomerCard(session.authToken, cardToRemove.id);
    setCardBusy(false);
    if (res?.data?.removed) {
      setSavedCards((prev) => prev.filter((c) => c.id !== cardToRemove.id));
      setCardToRemove(null);
    } else {
      setError(res?.error ?? "Could not remove that card.");
      setCardToRemove(null);
    }
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoggedIn) navigate("/account/login");
  }, [isLoggedIn, navigate]);

  // Load profile + returns + saved cards in parallel. Orders are deferred — see
  // loadOrders.
  useEffect(() => {
    if (!session?.authToken) return;

    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [profileResult, returnsResult, cardsResult] = await Promise.all([
          getCustomerProfile(session.authToken),
          getCustomerReturns(session.authToken),
          getCustomerCards(session.authToken),
        ]);

        if (profileResult?.data) {
          setProfile(profileResult.data);
          if (profileResult.data.address) {
            refreshSession({ savedAddress: profileResult.data.address });
          }
        }
        if (returnsResult?.data) setReturns(returnsResult.data);
        // Cards are a secondary concern: a failure here shows an empty wallet
        // rather than an error over the whole account page.
        if (cardsResult?.data) setSavedCards(cardsResult.data);
        if (profileResult?.error) setError(profileResult.error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [session?.authToken, refreshSession]);

  // On-demand order history. Safe to call again to re-fetch (the "Refresh"
  // button) — `ordersLoaded` only gates the initial prompt.
  const loadOrders = useCallback(async () => {
    if (!session?.authToken) return;
    setOrdersLoading(true);
    setOrdersError(null);
    const res = await getCustomerOrders(session.authToken);
    if (res?.data) {
      setOrders(res.data);
      setOrdersLoaded(true);
      ordersCache = {
        customerId: session.customerId ?? null,
        orders: res.data,
      };
    } else {
      setOrdersError(res?.error ?? "Could not load your order history.");
    }
    setOrdersLoading(false);
  }, [session?.authToken, session?.customerId]);

  const handleLogout = () => {
    ordersCache = null;
    logout();
    navigate("/home");
  };

  const handleProfileSaved = (updated: CustomerProfile) => {
    setProfile(updated);
    refreshSession({
      firstName: updated.firstName,
      lastName: updated.lastName,
      phone: updated.phone,
      savedAddress: updated.address,
    });
  };

  if (!isLoggedIn || !session) return null;

  const fullName = [session.firstName, session.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  // Greet by first name only when we actually have one — falling back to the
  // email here turned the heading into "Welcome back, someone@example.com".
  const greetingName = session.firstName?.trim() || "";
  // Monogram for the avatar: initials when named, else the email's first letter.
  const initials =
    [session.firstName, session.lastName]
      .filter(Boolean)
      .map((n) => n!.trim()[0])
      .join("")
      .toUpperCase() || session.email[0]?.toUpperCase();

  const tabs: { key: typeof activeTab; label: string; count?: number }[] = [
    {
      key: "orders",
      label: "Order History",
      count: orders.length || undefined,
    },
    { key: "returns", label: "Returns", count: returns.length || undefined },
    {
      key: "payment",
      label: "Payment Methods",
      count: savedCards.length || undefined,
    },
    { key: "profile", label: "Profile" },
  ];

  // Arrow-key navigation within the tablist (WAI-ARIA tabs pattern). Selection
  // follows focus, which is the expected behavior when switching panels is
  // cheap — none of these panels fetches anything on activation.
  const onTabKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const offset =
      e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : null;
    let next: number;
    if (offset !== null) next = (index + offset + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    else return;

    e.preventDefault();
    const { key } = tabs[next];
    setActiveTab(key);
    document.getElementById(`account-tab-${key}`)?.focus();
  };

  return (
    <>
      {isLoading && <Spinner text="Loading Account..." />}
      <PageContainer hasPadding gapSize={GapSize.Small}>
        {/* Centered, but at a generous 1280px — the account page is orders and
            forms, not dense tables, so it reads better with a measure. The
            admin portal is the one that goes edge-to-edge. */}
        <div className="max-w-7xl mx-auto w-full flex flex-col gap-6">
          {/* Account Header */}
          <OrderWrapper wide>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <div
                  aria-hidden="true"
                  className="hidden sm:flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-cream-dark text-brown-dark text-xl font-semibold opensans-text"
                >
                  {initials}
                </div>
                <div className="min-w-0">
                  <h1 className="text-3xl playfair-text text-brown-dark">
                    Welcome back{greetingName ? `, ${greetingName}` : ""}
                  </h1>
                  <p className="text-sm text-gray-600 lora-text mt-1 break-words">
                    {fullName ? `${fullName} · ` : ""}
                    {session.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
                {isAdmin && (
                  <Link to="/admin" className="btn-primary text-sm">
                    Admin Portal
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="btn-secondary text-sm"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </OrderWrapper>

          {error && <p className="text-rust text-sm text-center">{error}</p>}

          {/* Tab Navigation — underline tabs, left-aligned at their natural
              width and seated on a full-width hairline. Equal-width segments
              stretched across the page read as a toolbar, not navigation. */}
          <div
            role="tablist"
            aria-label="Account sections"
            className="flex flex-wrap gap-6 border-b border-accent-light"
          >
            {tabs.map(({ key, label, count }, index) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  id={`account-tab-${key}`}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`account-panel-${key}`}
                  // Roving tabindex: the tablist is ONE tab stop, and the
                  // arrow keys move between tabs inside it. Leaving every tab
                  // focusable is the usual `role="tab"` mistake — it makes a
                  // keyboard user tab through all three to reach the panel.
                  tabIndex={isActive ? 0 : -1}
                  onKeyDown={(e) => onTabKeyDown(e, index)}
                  onClick={() => setActiveTab(key)}
                  className={`group relative -mb-px flex shrink-0 items-center gap-2 border-b-2 px-1 pb-3 pt-2 text-sm font-semibold opensans-text transition-colors ${
                    isActive
                      ? "border-brown text-brown-dark"
                      : "border-transparent text-gray-500 hover:border-accent hover:text-brown-dark"
                  }`}
                >
                  {label}
                  {count !== undefined && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums transition-colors ${
                        isActive
                          ? "bg-cream-dark text-brown-dark"
                          : "bg-cream text-gray-500 group-hover:text-brown-dark"
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Orders Tab */}
          {activeTab === "orders" && (
            <div
              id="account-panel-orders"
              role="tabpanel"
              aria-labelledby="account-tab-orders"
              className="flex flex-col gap-4"
            >
              {!ordersLoaded && (
                <OrderWrapper wide>
                  <div className="flex flex-col items-center text-center py-10">
                    <div
                      aria-hidden="true"
                      className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-cream-dark text-2xl"
                    >
                      🧾
                    </div>
                    <h2 className="playfair-text text-xl text-brown-dark">
                      Your order history
                    </h2>
                    <p className="text-gray-500 text-sm mt-1 max-w-sm">
                      We only fetch it when you ask, to keep this page quick.
                    </p>
                    <button
                      onClick={loadOrders}
                      disabled={ordersLoading}
                      className="mt-5 btn-primary text-sm disabled:opacity-50"
                    >
                      {ordersLoading ? "Loading…" : "View order history"}
                    </button>
                    {ordersError && (
                      <p className="text-rust text-sm mt-3">{ordersError}</p>
                    )}
                  </div>
                </OrderWrapper>
              )}

              {ordersLoaded && (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-gray-500 lora-text">
                    {orders.length} order{orders.length === 1 ? "" : "s"}
                  </p>
                  <button
                    onClick={loadOrders}
                    disabled={ordersLoading}
                    className="btn-secondary text-sm disabled:opacity-50"
                  >
                    {ordersLoading ? "Refreshing…" : "Refresh"}
                  </button>
                </div>
              )}
              {ordersLoaded && ordersError && (
                <p className="text-rust text-sm text-center">{ordersError}</p>
              )}

              {ordersLoaded && orders.length === 0 && (
                <OrderWrapper wide>
                  <div className="flex flex-col items-center text-center py-10">
                    <div
                      aria-hidden="true"
                      className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-cream-dark text-2xl"
                    >
                      🇺🇸
                    </div>
                    <h2 className="playfair-text text-xl text-brown-dark">
                      No orders yet
                    </h2>
                    <p className="text-gray-500 text-sm mt-1 max-w-sm">
                      When you place an order it will appear here.
                    </p>
                    <Link to="/home" className="mt-5 btn-primary text-sm">
                      Shop Now
                    </Link>
                  </div>
                </OrderWrapper>
              )}

              {/* One card, hairline-divided rows — 21 separately bordered
                  cards each holding another bordered box read as clutter. */}
              {orders.length > 0 && (
                <OrderWrapper wide>
                  <ul className="divide-y divide-accent-light">
                    {orders.map((order) => {
                      const fulfillmentState = order.fulfillments?.[0]?.state;
                      const displayState = fulfillmentState ?? order.state;
                      const total = order.totalMoney?.amount;
                      const hasReview = !!(order as any).metadata
                        ?.review_rating;
                      const extra = (order.lineItems?.length ?? 0) - 2;

                      return (
                        <li
                          key={order.id}
                          className="flex flex-col lg:flex-row lg:items-center gap-4 py-4 first:pt-0 last:pb-0"
                        >
                          {/* Date + status */}
                          <div className="lg:w-44 lg:shrink-0 flex flex-col gap-1 min-w-0">
                            <p className="text-sm font-semibold text-brown-dark opensans-text">
                              {formatDate(order.createdAt)}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Tooltip
                                tooltip={fulfillmentDescription(displayState)}
                                position="bottom"
                                multiline
                              >
                                <span
                                  className={`${fulfillmentBadgeClass(
                                    displayState,
                                  )} cursor-help`}
                                >
                                  {fulfillmentLabel(displayState)}
                                </span>
                              </Tooltip>
                              {hasReview && (
                                <span className="text-xs text-yellow-600">
                                  ★ Reviewed
                                </span>
                              )}
                            </div>
                          </div>

                          {/* What was in it */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-700 truncate">
                              {(order.lineItems ?? [])
                                .slice(0, 2)
                                .map(
                                  (item) =>
                                    `${item.name}${
                                      Number(item.quantity) > 1
                                        ? ` × ${item.quantity}`
                                        : ""
                                    }`,
                                )
                                .join(", ")}
                              {extra > 0 && (
                                <span className="text-gray-400">
                                  {" "}
                                  +{extra} more
                                </span>
                              )}
                            </p>
                            <p
                              className="text-xs text-gray-400 font-mono truncate mt-0.5"
                              translate="no"
                            >
                              #{order.id}
                            </p>
                            {reorderMsg && reorderMsg.orderId === order.id && (
                              <p
                                className={`mt-1 text-xs ${
                                  reorderMsg.ok ? "text-green-700" : "text-rust"
                                }`}
                              >
                                {reorderMsg.text}
                              </p>
                            )}
                          </div>

                          {/* Total */}
                          <span className="text-lg font-semibold text-brown-dark tabular-nums lg:w-24 lg:text-right lg:shrink-0">
                            {formatMoney(total)}
                          </span>

                          {/* Actions — no Track button here: the detail page
                              already shows the tracking number, a carrier link
                              and its own Track Shipment link, and Track was a
                              dead end on canceled orders. */}
                          <div className="flex gap-2 flex-wrap lg:shrink-0">
                            <Link
                              to={`/account/orders/${order.id}`}
                              className="btn-secondary text-xs px-3 py-1.5"
                            >
                              Details
                            </Link>
                            <button
                              onClick={() => handleReorder(order)}
                              className="btn-primary text-xs px-3 py-1.5"
                            >
                              Buy Again
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </OrderWrapper>
              )}
            </div>
          )}

          {/* Returns Tab */}
          {activeTab === "returns" && (
            <div
              id="account-panel-returns"
              role="tabpanel"
              aria-labelledby="account-tab-returns"
              className="flex flex-col gap-4"
            >
              {returns.length === 0 ? (
                <OrderWrapper wide>
                  <div className="flex flex-col items-center text-center py-10">
                    <div
                      aria-hidden="true"
                      className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-cream-dark text-2xl"
                    >
                      📦
                    </div>
                    <h2 className="playfair-text text-xl text-brown-dark">
                      No return requests yet
                    </h2>
                    <p className="text-gray-500 text-sm mt-1 max-w-sm">
                      Start a return from your order detail page.
                    </p>
                  </div>
                </OrderWrapper>
              ) : (
                <div className="flex flex-col gap-4">
                  {returns.map((req) => {
                    const statusMeta: Record<
                      ReturnRequest["status"],
                      { label: string; chip: string }
                    > = {
                      requested: {
                        label: "Pending review",
                        chip: "bg-amber-100 text-amber-800",
                      },
                      approved: {
                        label: "Approved — ship it back",
                        chip: "bg-blue-100 text-blue-800",
                      },
                      received: {
                        label: "Item received",
                        chip: "bg-purple-100 text-purple-800",
                      },
                      denied: {
                        label: "Denied",
                        chip: "bg-red-100 text-red-800",
                      },
                      refunded: {
                        label: "Refunded",
                        chip: "bg-green-100 text-green-800",
                      },
                      canceled: {
                        label: "Canceled",
                        chip: "bg-gray-100 text-gray-600",
                      },
                    };
                    const meta = statusMeta[req.status] ?? statusMeta.requested;
                    // Customer can call off a return that hasn't progressed yet.
                    const cancelable =
                      req.status === "requested" || req.status === "approved";
                    return (
                      <OrderWrapper key={req.id} wide>
                        <div className="flex flex-col sm:flex-row justify-between gap-3">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.chip}`}
                              >
                                {meta.label}
                              </span>
                            </div>
                            <p
                              className="text-xs text-gray-400 font-mono mt-1"
                              translate="no"
                            >
                              Order #{req.originalOrderId}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(req.createdAt)}
                            </p>
                          </div>
                          <div className="flex flex-col items-start sm:items-end gap-1">
                            <ul className="text-sm text-gray-700 flex flex-col gap-0.5">
                              {req.items.map((item, i) => (
                                <li key={i}>
                                  {item.name} &times; {item.quantity}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        {req.adminNote && (
                          <div className="mt-2 border-t border-accent-light pt-2">
                            <p className="text-xs text-gray-600">
                              <span className="font-semibold">
                                Note from us:
                              </span>{" "}
                              {req.adminNote}
                            </p>
                          </div>
                        )}
                        {cancelable && (
                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={() => setCancelTarget(req)}
                              className="text-xs font-semibold text-rust underline hover:text-red-700"
                            >
                              Cancel this return
                            </button>
                          </div>
                        )}
                      </OrderWrapper>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Payment Methods Tab */}
          {activeTab === "payment" && (
            <div
              id="account-panel-payment"
              role="tabpanel"
              aria-labelledby="account-tab-payment"
            >
              <OrderWrapper wide>
                <h2 className="text-xl playfair-text text-brown-dark">
                  Saved Cards
                </h2>
                <div className="w-full h-px bg-accent-light" />
                {savedCards.length === 0 ? (
                  <div className="flex flex-col items-center text-center py-10">
                    <div
                      aria-hidden="true"
                      className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-cream-dark text-2xl"
                    >
                      💳
                    </div>
                    <h3 className="playfair-text text-lg text-brown-dark">
                      No saved cards
                    </h3>
                    <p className="text-gray-500 text-sm mt-1 max-w-sm">
                      Tick &ldquo;Save my information for a faster
                      checkout&rdquo; while paying and your card will show up
                      here for next time.
                    </p>
                  </div>
                ) : (
                  <>
                    <ul className="flex flex-col gap-3">
                      {savedCards.map((card) => {
                        const expiry = savedCardExpiry(card);
                        const expired = isSavedCardExpired(card);
                        return (
                          <li
                            key={card.id}
                            className="flex items-center justify-between gap-4 rounded border border-gray-300 p-3"
                          >
                            <span className="text-sm">
                              {/* Card digits and dates are data — never let a
                                  page translation rewrite them. */}
                              <span className="font-medium" translate="no">
                                {savedCardLabel(card)}
                              </span>
                              <span className="block text-gray-500 text-xs">
                                {expiry && (
                                  <span translate="no">
                                    {expired ? "Expired" : "Expires"} {expiry}
                                  </span>
                                )}
                                {card.cardholderName && (
                                  <span className="block">
                                    {card.cardholderName}
                                  </span>
                                )}
                              </span>
                            </span>
                            <div className="flex items-center gap-3">
                              {/* An expired card is still listed so it can be
                                  cleared out — checkout simply won't offer it. */}
                              {expired && (
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                                  Expired
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => setCardToRemove(card)}
                                className="text-xs font-semibold text-rust underline hover:text-red-700"
                              >
                                Remove
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="text-gray-500 text-xs mt-4">
                      Your full card number is never stored on this site — it
                      stays with Square, our payment processor. We only keep the
                      brand, last four digits, and expiry so you can recognize
                      the card.
                    </p>
                  </>
                )}
              </OrderWrapper>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div
              id="account-panel-profile"
              role="tabpanel"
              aria-labelledby="account-tab-profile"
            >
              <OrderWrapper wide>
                <h2 className="text-xl playfair-text text-brown-dark">
                  Your Information
                </h2>
                <div className="w-full h-px bg-accent-light" />
                {profile ? (
                  <ProfileForm
                    profile={profile}
                    authToken={session.authToken}
                    onSaved={handleProfileSaved}
                  />
                ) : (
                  <p className="text-gray-500 text-sm">Loading profile…</p>
                )}
              </OrderWrapper>
            </div>
          )}
        </div>
      </PageContainer>

      <ConfirmationModal
        isOpen={!!cancelTarget}
        onConfirm={cancelBusy ? undefined : handleCancelReturn}
        onCancel={cancelBusy ? undefined : () => setCancelTarget(null)}
      >
        {cancelBusy ? (
          "Canceling your return request…"
        ) : (
          <>
            Cancel your return request for order{" "}
            <strong translate="no">#{cancelTarget?.originalOrderId}</strong>?
            You&apos;ll keep your item(s) and no refund will be issued. You can
            always start a new return later while your order is still eligible.
          </>
        )}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={!!cardToRemove}
        onConfirm={cardBusy ? undefined : handleRemoveCard}
        onCancel={cardBusy ? undefined : () => setCardToRemove(null)}
      >
        {cardBusy ? (
          "Removing your card…"
        ) : (
          <>
            Remove{" "}
            <strong translate="no">
              {cardToRemove ? savedCardLabel(cardToRemove) : ""}
            </strong>{" "}
            from your account? This can&apos;t be undone — you&apos;ll need to
            enter the card again at your next checkout. Your past orders are
            unaffected.
          </>
        )}
      </ConfirmationModal>
    </>
  );
};

export default AccountPage;
